// src/pages/affiliate/AnalyticsPage.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { useUI } from '../../context/UIContext';
import { getBaseUrl } from '../../config/api';
import '../../styles/AffiliateAnalytics.css';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface AnalyticsData {
  dateRange: {
    start: string;
    end: string;
  };
  clickAnalytics: {
    totalClicks: number;
    totalConversions: number;
    conversionRate: number;
    deviceBreakdown: string[];
    countryBreakdown: string[];
  };
  commissionAnalytics: {
    totalCommissions: number;
    totalEarnings: number;
    averageCommission: number;
    byStatus: {
      pending: number;
      approved: number;
      paid: number;
      cancelled: number;
    };
  };
  timeSeriesData: {
    labels: string[];
    clicks: number[];
    conversions: number[];
    earnings: number[];
  };
  topPerformingLinks: {
    landingPage: string;
    clicks: number;
    conversions: number;
    conversionRate: number;
  }[];
  conversionFunnel?: {
    steps: {
      name: string;
      count: number;
      percentage: number;
      dropoff: number;
    }[];
    overallConversionRate: number;
    totalClicks: number;
    totalCommissions: number;
  };
  cohortAnalysis?: {
    cohorts: {
      cohort: string;
      signups: number;
      bookings: number;
      completedBookings: number;
      commissions: number;
      totalRevenue: number;
      averageRevenuePerUser: number;
      retentionRate: number;
    }[];
    summary: {
      totalCohorts: number;
      totalSignups: number;
      totalBookings: number;
      totalCommissions: number;
      totalRevenue: number;
      averageRevenuePerCohort: number;
      averageSignupsPerCohort: number;
    };
  };
}

const AffiliateAnalyticsPage: React.FC = () => {
  const { showSnackbar } = useUI();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [groupBy, setGroupBy] = useState<'day' | 'week' | 'month'>('day');

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange, groupBy]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAnalytics = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('affiliateToken') || sessionStorage.getItem('affiliateToken');
      
      if (!token) {
        showSnackbar('Please log in to view analytics', 'error');
        navigate('/affiliate/login');
        return;
      }

      const queryParams = new URLSearchParams({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        groupBy
      });

      const response = await fetch(
        `${getBaseUrl()}/v1/affiliate/analytics?${queryParams}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        // Ensure all required fields have defaults
        const analyticsData = {
          ...data.data,
          clickAnalytics: {
            totalClicks: data.data.clickAnalytics?.totalClicks || 0,
            totalConversions: data.data.clickAnalytics?.totalConversions || 0,
            conversionRate: data.data.clickAnalytics?.conversionRate || 0,
            deviceBreakdown: data.data.clickAnalytics?.deviceBreakdown || [],
            countryBreakdown: data.data.clickAnalytics?.countryBreakdown || []
          },
          commissionAnalytics: {
            totalCommissions: data.data.commissionAnalytics?.totalCommissions || 0,
            totalEarnings: data.data.commissionAnalytics?.totalEarnings || 0,
            averageCommission: data.data.commissionAnalytics?.averageCommission || 0,
            byStatus: {
              pending: data.data.commissionAnalytics?.byStatus?.pending || 0,
              approved: data.data.commissionAnalytics?.byStatus?.approved || 0,
              paid: data.data.commissionAnalytics?.byStatus?.paid || 0,
              cancelled: data.data.commissionAnalytics?.byStatus?.cancelled || 0
            }
          },
          timeSeriesData: {
            labels: data.data.timeSeriesData?.labels || [],
            clicks: data.data.timeSeriesData?.clicks || [],
            conversions: data.data.timeSeriesData?.conversions || [],
            earnings: data.data.timeSeriesData?.earnings || []
          },
          topPerformingLinks: data.data.topPerformingLinks || []
        };
        setAnalytics(analyticsData);
      } else {
        if (response.status === 401 || response.status === 403) {
          localStorage.removeItem('affiliateToken');
          sessionStorage.removeItem('affiliateToken');
          showSnackbar('Session expired. Please log in again.', 'error');
          navigate('/affiliate/login');
          return;
        }
        showSnackbar(data.message || 'Failed to load analytics', 'error');
      }
    } catch (error) {
      console.error('Analytics fetch error:', error);
      showSnackbar('An error occurred loading analytics', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getDeviceBreakdown = () => {
    if (!analytics?.clickAnalytics?.deviceBreakdown || !Array.isArray(analytics.clickAnalytics.deviceBreakdown)) {
      return null;
    }

    const devices = analytics.clickAnalytics.deviceBreakdown;
    const counts: { [key: string]: number } = {};
    
    devices.forEach(device => {
      counts[device] = (counts[device] || 0) + 1;
    });

    return {
      labels: Object.keys(counts),
      data: Object.values(counts),
      backgroundColor: [
        'rgba(102, 126, 234, 0.8)',
        'rgba(72, 187, 120, 0.8)',
        'rgba(237, 137, 54, 0.8)',
        'rgba(239, 68, 68, 0.8)'
      ]
    };
  };

  const getCountryBreakdown = () => {
    if (!analytics?.clickAnalytics?.countryBreakdown || !Array.isArray(analytics.clickAnalytics.countryBreakdown)) {
      return null;
    }

    const countries = analytics.clickAnalytics.countryBreakdown;
    const counts: { [key: string]: number } = {};
    
    countries.forEach(country => {
      if (country) {
        counts[country] = (counts[country] || 0) + 1;
      }
    });

    // Get top 10 countries
    const sorted = Object.entries(counts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10);

    return {
      labels: sorted.map(([country]) => country),
      data: sorted.map(([, count]) => count),
      backgroundColor: 'rgba(102, 126, 234, 0.8)'
    };
  };

  const getTimeSeriesChartData = () => {
    if (!analytics?.timeSeriesData || !analytics.timeSeriesData.labels || analytics.timeSeriesData.labels.length === 0) {
      return null;
    }

    return {
      labels: analytics.timeSeriesData.labels || [],
      datasets: [
        {
          label: 'Clicks',
          data: analytics.timeSeriesData.clicks || [],
          borderColor: 'rgb(102, 126, 234)',
          backgroundColor: 'rgba(102, 126, 234, 0.1)',
          fill: true,
          tension: 0.4
        },
        {
          label: 'Conversions',
          data: analytics.timeSeriesData.conversions || [],
          borderColor: 'rgb(72, 187, 120)',
          backgroundColor: 'rgba(72, 187, 120, 0.1)',
          fill: true,
          tension: 0.4
        },
        {
          label: 'Earnings ($)',
          data: analytics.timeSeriesData.earnings || [],
          borderColor: 'rgb(237, 137, 54)',
          backgroundColor: 'rgba(237, 137, 54, 0.1)',
          fill: true,
          tension: 0.4,
          yAxisID: 'y1'
        }
      ]
    };
  };

  if (isLoading) {
    return (
      <div className="affiliate-analytics__loading">
        <div className="spinner"></div>
        <p>Loading analytics...</p>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="affiliate-analytics__error">
        <p>Failed to load analytics</p>
        <button onClick={fetchAnalytics} className="btn-primary">Retry</button>
      </div>
    );
  }

  // Ensure we have valid analytics data with defaults
  const safeAnalytics = {
    clickAnalytics: analytics.clickAnalytics || {
      totalClicks: 0,
      totalConversions: 0,
      conversionRate: 0,
      deviceBreakdown: [],
      countryBreakdown: []
    },
    commissionAnalytics: analytics.commissionAnalytics || {
      totalCommissions: 0,
      totalEarnings: 0,
      averageCommission: 0,
      byStatus: {
        pending: 0,
        approved: 0,
        paid: 0,
        cancelled: 0
      }
    },
    timeSeriesData: analytics.timeSeriesData || {
      labels: [],
      clicks: [],
      conversions: [],
      earnings: []
    },
    topPerformingLinks: analytics.topPerformingLinks || []
  };

  // Use safe analytics data for all operations
  const analyticsToUse = safeAnalytics;
  
  const deviceData = getDeviceBreakdown();
  const countryData = getCountryBreakdown();
  const timeSeriesData = getTimeSeriesChartData();

  return (
    <div className="affiliate-analytics">
      <div className="affiliate-analytics__header">
        <div>
          <h1>Detailed Analytics</h1>
          <p>In-depth insights into your referral performance</p>
        </div>
        <button onClick={() => navigate('/affiliate/dashboard')} className="btn-secondary">
          <i className="fas fa-arrow-left"></i> Back to Dashboard
        </button>
      </div>

      {/* Date Range Selector */}
      <div className="analytics-controls">
        <div className="control-group">
          <label>Start Date</label>
          <input
            type="date"
            value={dateRange.startDate}
            onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
            className="form-input"
          />
        </div>
        <div className="control-group">
          <label>End Date</label>
          <input
            type="date"
            value={dateRange.endDate}
            onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
            className="form-input"
          />
        </div>
        <div className="control-group">
          <label>Group By</label>
          <select
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value as 'day' | 'week' | 'month')}
            className="form-input"
          >
            <option value="day">Day</option>
            <option value="week">Week</option>
            <option value="month">Month</option>
          </select>
        </div>
        <button onClick={fetchAnalytics} className="btn-primary">
          <i className="fas fa-sync"></i> Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="analytics-summary">
        <div className="summary-card">
          <h3>Total Clicks</h3>
          <p className="summary-value">{(analyticsToUse.clickAnalytics.totalClicks || 0).toLocaleString()}</p>
        </div>
        <div className="summary-card">
          <h3>Total Conversions</h3>
          <p className="summary-value">{(analyticsToUse.clickAnalytics.totalConversions || 0).toLocaleString()}</p>
        </div>
        <div className="summary-card">
          <h3>Conversion Rate</h3>
          <p className="summary-value">{((analyticsToUse.clickAnalytics.conversionRate || 0).toFixed(2))}%</p>
        </div>
        <div className="summary-card">
          <h3>Total Earnings</h3>
          <p className="summary-value">{formatCurrency(analyticsToUse.commissionAnalytics.totalEarnings || 0)}</p>
        </div>
      </div>

      {/* Time Series Chart */}
      {timeSeriesData && timeSeriesData.labels && timeSeriesData.labels.length > 0 ? (
        <div className="analytics-chart">
          <h2>Performance Over Time</h2>
          <Line
            data={timeSeriesData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { position: 'top' },
                title: { display: true, text: 'Clicks, Conversions, and Earnings Over Time' }
              },
              scales: {
                y: {
                  type: 'linear',
                  display: true,
                  position: 'left',
                  title: { display: true, text: 'Clicks / Conversions' },
                  beginAtZero: true
                },
                y1: {
                  type: 'linear',
                  display: true,
                  position: 'right',
                  title: { display: true, text: 'Earnings ($)' },
                  grid: { drawOnChartArea: false },
                  beginAtZero: true
                }
              }
            }}
          />
        </div>
      ) : (
        <div className="analytics-chart">
          <h2>Performance Over Time</h2>
          <div className="chart-empty-state">
            <i className="fas fa-chart-line"></i>
            <p>No data available for the selected period</p>
            <small>Start sharing your referral link to see performance data</small>
          </div>
        </div>
      )}

      {/* Device & Country Breakdown */}
      <div className="analytics-breakdown">
        {deviceData && deviceData.labels && deviceData.labels.length > 0 ? (
          <div className="breakdown-chart">
            <h2>Device Breakdown</h2>
            <Bar
              data={{
                labels: deviceData.labels,
                datasets: [{
                  label: 'Clicks by Device',
                  data: deviceData.data,
                  backgroundColor: deviceData.backgroundColor
                }]
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false },
                  title: { display: true, text: 'Clicks by Device Type' }
                },
                scales: {
                  y: {
                    beginAtZero: true
                  }
                }
              }}
            />
          </div>
        ) : (
          <div className="breakdown-chart">
            <h2>Device Breakdown</h2>
            <div className="chart-empty-state">
              <i className="fas fa-mobile-alt"></i>
              <p>No device data available</p>
            </div>
          </div>
        )}

        {countryData && countryData.labels && countryData.labels.length > 0 ? (
          <div className="breakdown-chart">
            <h2>Top Countries</h2>
            <Bar
              data={{
                labels: countryData.labels,
                datasets: [{
                  label: 'Clicks by Country',
                  data: countryData.data,
                  backgroundColor: countryData.backgroundColor
                }]
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false },
                  title: { display: true, text: 'Top 10 Countries by Clicks' }
                },
                indexAxis: 'y',
                scales: {
                  x: {
                    beginAtZero: true
                  }
                }
              }}
            />
          </div>
        ) : (
          <div className="breakdown-chart">
            <h2>Top Countries</h2>
            <div className="chart-empty-state">
              <i className="fas fa-globe"></i>
              <p>No country data available</p>
            </div>
          </div>
        )}
      </div>

      {/* Top Performing Links */}
      {analyticsToUse.topPerformingLinks && analyticsToUse.topPerformingLinks.length > 0 ? (
        <div className="analytics-links">
          <h2>Top Performing Links</h2>
          <div className="links-table">
            <table>
              <thead>
                <tr>
                  <th>Landing Page</th>
                  <th>Clicks</th>
                  <th>Conversions</th>
                  <th>Conversion Rate</th>
                </tr>
              </thead>
              <tbody>
                {analyticsToUse.topPerformingLinks.map((link, index) => (
                  <tr key={index}>
                    <td>
                      <code>{link.landingPage || 'N/A'}</code>
                    </td>
                    <td>{link.clicks || 0}</td>
                    <td>{link.conversions || 0}</td>
                    <td>{((link.conversionRate || 0).toFixed(2))}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="analytics-links">
          <h2>Top Performing Links</h2>
          <div className="links-empty-state">
            <i className="fas fa-link"></i>
            <p>No link performance data available</p>
            <small>Start sharing your referral links to see which perform best</small>
          </div>
        </div>
      )}

      {/* Commission Status Breakdown */}
      <div className="analytics-commissions">
        <h2>Commission Status Breakdown</h2>
        <div className="commission-status-grid">
          <div className="status-item">
            <span className="status-label">Pending</span>
            <span className="status-value">{analyticsToUse.commissionAnalytics.byStatus.pending || 0}</span>
          </div>
          <div className="status-item">
            <span className="status-label">Approved</span>
            <span className="status-value">{analyticsToUse.commissionAnalytics.byStatus.approved || 0}</span>
          </div>
          <div className="status-item">
            <span className="status-label">Paid</span>
            <span className="status-value">{analyticsToUse.commissionAnalytics.byStatus.paid || 0}</span>
          </div>
          <div className="status-item">
            <span className="status-label">Cancelled</span>
            <span className="status-value">{analyticsToUse.commissionAnalytics.byStatus.cancelled || 0}</span>
          </div>
        </div>
      </div>

      {/* Conversion Funnel */}
      {analytics?.conversionFunnel && analytics.conversionFunnel.steps && analytics.conversionFunnel.steps.length > 0 ? (
        <div className="analytics-funnel">
          <h2>Conversion Funnel</h2>
          <p className="funnel-description">
            Track how users progress from clicks to commissions. Overall conversion rate: <strong>{analytics.conversionFunnel.overallConversionRate.toFixed(2)}%</strong>
          </p>
          <div className="funnel-container">
            {analytics.conversionFunnel.steps.map((step, index) => {
              const widthPercentage = step.count > 0 && analytics.conversionFunnel!.totalClicks > 0
                ? ((step.count / analytics.conversionFunnel!.totalClicks) * 100).toFixed(1)
                : 0;
              
              return (
                <div key={index} className="funnel-step">
                  <div className="funnel-step-header">
                    <span className="funnel-step-name">{step.name}</span>
                    <span className="funnel-step-count">{step.count.toLocaleString()}</span>
                    <span className="funnel-step-percentage">{step.percentage.toFixed(1)}%</span>
                  </div>
                  <div className="funnel-bar-container">
                    <div 
                      className="funnel-bar"
                      style={{ width: `${widthPercentage}%` }}
                    >
                      <div className="funnel-bar-fill"></div>
                    </div>
                  </div>
                  {step.dropoff > 0 && index < analytics.conversionFunnel!.steps.length - 1 && (
                    <div className="funnel-dropoff">
                      <i className="fas fa-arrow-down"></i>
                      <span>{step.dropoff.toLocaleString()} dropped off</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="analytics-funnel">
          <h2>Conversion Funnel</h2>
          <div className="funnel-empty-state">
            <i className="fas fa-filter"></i>
            <p>No conversion funnel data available</p>
            <small>Start tracking referrals to see conversion metrics</small>
          </div>
        </div>
      )}

      {/* Cohort Analysis */}
      {analytics?.cohortAnalysis && analytics.cohortAnalysis.cohorts && analytics.cohortAnalysis.cohorts.length > 0 ? (
        <div className="analytics-cohort">
          <h2>Cohort Analysis</h2>
          <p className="cohort-description">
            Performance by signup month. {analytics.cohortAnalysis.summary.totalCohorts} cohort(s) analyzed.
          </p>
          
          {/* Cohort Summary */}
          <div className="cohort-summary">
            <div className="summary-item">
              <label>Total Signups</label>
              <span>{analytics.cohortAnalysis.summary.totalSignups.toLocaleString()}</span>
            </div>
            <div className="summary-item">
              <label>Total Bookings</label>
              <span>{analytics.cohortAnalysis.summary.totalBookings.toLocaleString()}</span>
            </div>
            <div className="summary-item">
              <label>Total Commissions</label>
              <span>{analytics.cohortAnalysis.summary.totalCommissions.toLocaleString()}</span>
            </div>
            <div className="summary-item">
              <label>Total Revenue</label>
              <span>{formatCurrency(analytics.cohortAnalysis.summary.totalRevenue)}</span>
            </div>
            <div className="summary-item">
              <label>Avg Revenue/Cohort</label>
              <span>{formatCurrency(Number(analytics.cohortAnalysis.summary.averageRevenuePerCohort))}</span>
            </div>
          </div>

          {/* Cohort Table */}
          <div className="cohort-table-container">
            <table className="cohort-table">
              <thead>
                <tr>
                  <th>Cohort (Month)</th>
                  <th>Signups</th>
                  <th>Bookings</th>
                  <th>Completed</th>
                  <th>Commissions</th>
                  <th>Revenue</th>
                  <th>Avg Revenue/User</th>
                  <th>Retention</th>
                </tr>
              </thead>
              <tbody>
                {analytics.cohortAnalysis.cohorts.map((cohort, index) => (
                  <tr key={index}>
                    <td><strong>{cohort.cohort}</strong></td>
                    <td>{cohort.signups.toLocaleString()}</td>
                    <td>{cohort.bookings.toLocaleString()}</td>
                    <td>{cohort.completedBookings.toLocaleString()}</td>
                    <td>{cohort.commissions.toLocaleString()}</td>
                    <td>{formatCurrency(cohort.totalRevenue)}</td>
                    <td>{formatCurrency(Number(cohort.averageRevenuePerUser))}</td>
                    <td>
                      <span className={`retention-badge ${cohort.retentionRate >= 50 ? 'high' : cohort.retentionRate >= 25 ? 'medium' : 'low'}`}>
                        {cohort.retentionRate.toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="analytics-cohort">
          <h2>Cohort Analysis</h2>
          <div className="cohort-empty-state">
            <i className="fas fa-users"></i>
            <p>No cohort data available</p>
            <small>Signups will be grouped by month for cohort analysis</small>
          </div>
        </div>
      )}
    </div>
  );
};

export default AffiliateAnalyticsPage;

